/**
 * Does a brand block's CDT give the base back, row by row? And where it does not, which line
 * explains the base?
 *
 * Studio reads a brand block's cdt2 rows off the base (the form, the sub-brand, the size tier, or
 * the level that reads best as rows). Sorting the block's products by those rows must give the
 * base's rows back: each row one run of shelves, the rows in their shipped order top to bottom, the
 * same test `measureShelfRowsOf` already makes for the size tier. Where the rows do not, the base
 * is grouping something the rows do not name. The explanation looked for is a line: products that
 * share one attribute value and stand alone as the block's top or bottom row, most of them there
 * (Tide's unscented pods on shelf 6 under its Small and Medium pods). Taken out as a row of their
 * own, the rest of the block is checked again; the fewest products that explain the base go first,
 * so a line never takes more of the block than the base shows. A block that still does not give
 * the base back, with no line left to explain it, is a question for the merchant.
 *
 * No threshold: "most of the line" is more of its products in the edge row than outside it, and a
 * line is the smaller part of its block. Only a level that groups products names a line (the
 * caller passes those), so a pack size or a code never does.
 */
import { type BlockingScope, measureShelfRowsOf } from "./blockOrientation";
import { SIZE_TIER_ATTR, SIZE_TIER_ORDER, SUB_BRAND_ATTR, sizeTierRank } from "./sizeTiers";
import { type StudioItem, isPlaceholderValue } from "./studioTypes";

/** A line the base stands as a brand block's top or bottom row. */
export interface RowLine {
  attr: string;
  value: string;
  /** The row's name: the sub-brand most of the line carries when no other product of the block
   *  carries it, else the line's own value. */
  name: string;
  side: "top" | "bottom";
  skuIds: readonly string[];
}

export interface RowReplication {
  /** The block's rows, as read, give the base back. */
  replicates: boolean;
  /** The lines that explain the base, in the order they were taken out. */
  lines: readonly RowLine[];
  /** True when the rows do not give the base back and no line explains what is left. */
  unexplained: boolean;
}

const valueOf = (item: StudioItem, attr: string): string => String(item.attrs[attr] ?? "");

/** One scope per run the items stand in. */
const scopesFor = (items: readonly StudioItem[], path: readonly string[]): BlockingScope[] => {
  const byRun = new Map<number, StudioItem[]>();
  for (const item of items)
    for (const run of new Set(item.placements.map((placement) => placement.run)))
      byRun.set(run, [...(byRun.get(run) ?? []), item]);
  return [...byRun].map(([run, scoped]) => ({ run, path, items: scoped }));
};

const sizeRank = (value: string): number | undefined => {
  const rank = sizeTierRank(value);
  return rank < SIZE_TIER_ORDER.length ? rank : undefined;
};

/** Each value of `attr` on one run of shelves, in order top to bottom: Small to Family for the
 *  size tier, any order for a level whose order is read off the base. */
const rowsHold = (items: readonly StudioItem[], attr: string): boolean => {
  const values = new Set(items.map((item) => valueOf(item, attr)).filter((v) => v !== ""));
  if (values.size < 2) return true;
  // On one shelf nothing is stacked: the values stand side by side.
  if (new Set(items.flatMap((item) => item.placements.map((p) => p.shelf))).size < 2) return true;
  const reading = measureShelfRowsOf(
    items,
    attr,
    attr === SIZE_TIER_ATTR ? sizeRank : () => undefined,
    scopesFor(items, []),
  );
  return reading.oneRun === 1 && reading.order === 1;
};

/** Products the base stacks by size: two or more size tiers, every product tagged, on more than
 *  one shelf, each size one run of shelves, no size centred above a smaller one and at least one
 *  size above another (`measureShelfRowsOf`: oneRun 1, inverted 0, level under 1). Neighbouring
 *  sizes may share a shelf: a base with few shelves stands each scent's Small beside its Medium
 *  on one shelf, Large below (Downy's liquids on fabric care 168). Sizes side by side on the
 *  same shelves are not stacked. */
export const stackedBySize = (items: readonly StudioItem[]): boolean => {
  const sizes = items.map((item) => valueOf(item, SIZE_TIER_ATTR));
  if (sizes.some((size) => size === "" || isPlaceholderValue(size))) return false;
  if (new Set(sizes).size < 2) return false;
  if (new Set(items.flatMap((item) => item.placements.map((p) => p.shelf))).size < 2) return false;
  const reading = measureShelfRowsOf(items, SIZE_TIER_ATTR, sizeRank, scopesFor(items, []));
  return reading.oneRun === 1 && reading.inverted === 0 && reading.level < 1;
};

/** The products split by one attribute's values. */
const groupsBy = (items: readonly StudioItem[], attr: string): StudioItem[][] => {
  const groups = new Map<string, StudioItem[]>();
  for (const item of items) {
    const value = valueOf(item, attr);
    groups.set(value, [...(groups.get(value) ?? []), item]);
  }
  return [...groups.values()];
};

/** `attr` only regroups these products' sub-brands: every sub-brand takes one of its values
 *  (Gain's liquids on base 210: Oxi "Oxi" is the Ultra OXI sub-brand; Tide's pods: Subbrand
 *  "Power" is Power PODS). Sub-brands stacked inside the size rows are the block's size layout
 *  (`sizeLayoutOf`), read and asked there, so such an attribute says nothing more. */
const regroupsSubBrands = (items: readonly StudioItem[], attr: string): boolean => {
  const valueOfSubBrand = new Map<string, string>();
  for (const item of items) {
    const subBrand = valueOf(item, SUB_BRAND_ATTR);
    const value = valueOf(item, attr);
    if ((valueOfSubBrand.get(subBrand) ?? value) !== value) return false;
    valueOfSubBrand.set(subBrand, value);
  }
  return true;
};

/** The base stacks these products by `attr`: on some run of shelves two or more of its values
 *  stand on shelves of their own, and on every run each value is one run of shelves that shares
 *  no shelf with another value (Gain's Small pods on base 210: "Soft" on shelves 1-2, "Hard" on
 *  shelf 3). Values that share a shelf stand side by side there, not stacked. An attribute that
 *  only regroups the products' sub-brands (`regroupsSubBrands`) does not stack them. */
const stackedBy = (items: readonly StudioItem[], attr: string): boolean => {
  if (regroupsSubBrands(items, attr)) return false;
  const byRun = new Map<number, Map<string, Set<number>>>();
  for (const item of items) {
    const value = valueOf(item, attr);
    if (value === "" || isPlaceholderValue(value)) continue;
    for (const { run, shelf } of item.placements) {
      const values = byRun.get(run) ?? new Map<string, Set<number>>();
      values.set(value, (values.get(value) ?? new Set<number>()).add(shelf));
      byRun.set(run, values);
    }
  }
  let stacked = false;
  for (const values of byRun.values()) {
    if (values.size < 2) continue;
    const seen = new Set<number>();
    for (const shelves of values.values()) {
      const sorted = [...shelves].sort((x, y) => x - y);
      if (sorted[sorted.length - 1] - sorted[0] !== sorted.length - 1) return false;
      if (sorted.some((shelf) => seen.has(shelf))) return false;
      for (const shelf of sorted) seen.add(shelf);
    }
    stacked = true;
  }
  return stacked;
};

/** The block's rows give the base back: the rows, inside each row its size rows where the size
 *  tier reads inside the rows (`innerSize`), and no row whose products the base stacks by one
 *  more attribute of `stackAttrs` (Gain's pods on base 210: inside Small, Pack type "Soft" on
 *  shelves 1-2 and "Hard" on shelf 3; inside Medium, Oxi "No oxi" on shelves 3-4 and "Oxi" on
 *  5-6). Sorted by the rows alone, a store planogram mixes such a row. */
const replicates = (
  items: readonly StudioItem[],
  rowAttr: string,
  innerSize: boolean,
  stackAttrs: readonly string[],
): boolean => {
  if (!rowsHold(items, rowAttr)) return false;
  const rows = groupsBy(items, rowAttr);
  if (innerSize && !rows.every((row) => rowsHold(row, SIZE_TIER_ATTR))) return false;
  const finest = innerSize ? rows.flatMap((row) => groupsBy(row, SIZE_TIER_ATTR)) : rows;
  return finest.every((row) =>
    stackAttrs.every((attr) => attr === rowAttr || !stackedBy(row, attr)),
  );
};

/** Every line of `items`: the products sharing one value of one attribute that stand alone as
 *  the top or bottom row, most of them there, and cut across the rows as read (a line that is a
 *  whole row already is that row). Bottom row first, then the attributes in level order. */
const edgeLinesOf = (
  items: readonly StudioItem[],
  rowAttr: string,
  attrs: readonly string[],
): RowLine[] => {
  const shelves = [...new Set(items.flatMap((item) => item.placements.map((p) => p.shelf)))].sort(
    (a, b) => a - b,
  );
  if (shelves.length < 2) return [];
  const onShelf = (shelf: number) =>
    items.filter((item) => item.placements.some((placement) => placement.shelf === shelf));
  const found: RowLine[] = [];
  for (const side of ["bottom", "top"] as const) {
    const fromEdge = side === "bottom" ? [...shelves].reverse() : shelves;
    for (const attr of attrs) {
      if (attr === rowAttr) continue;
      // The run of shelves from the edge where every product shares one value.
      let value: string | null = null;
      const run: number[] = [];
      for (const shelf of fromEdge) {
        const values = new Set(onShelf(shelf).map((item) => valueOf(item, attr)));
        if (values.size !== 1) break;
        const [only] = values;
        if (isPlaceholderValue(only) || (value !== null && only !== value)) break;
        value = only;
        run.push(shelf);
      }
      if (value === null || run.length === shelves.length) continue;
      const line = items.filter((item) => valueOf(item, attr) === value);
      const inRow = line.filter((item) =>
        item.placements.some((placement) => run.includes(placement.shelf)),
      );
      // A line is the part of the block that stands apart: fewer products than the rest, and most
      // of its own products in the edge row.
      if (line.length * 2 >= items.length || inRow.length * 2 <= line.length) continue;
      // A line that is exactly one or more whole rows already stands as those rows.
      const lineIds = new Set(line.map((item) => item.skuId));
      const rowsTouched = new Set(line.map((item) => valueOf(item, rowAttr)));
      const cuts = items.some(
        (item) => !lineIds.has(item.skuId) && rowsTouched.has(valueOf(item, rowAttr)),
      );
      if (!cuts) continue;
      const subBrands = new Map<string, number>();
      for (const item of line) {
        const subBrand = valueOf(item, SUB_BRAND_ATTR);
        if (subBrand !== "") subBrands.set(subBrand, (subBrands.get(subBrand) ?? 0) + 1);
      }
      const [subBrand, count] = [...subBrands].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];
      const ownSubBrand =
        count * 2 > line.length &&
        !items.some(
          (item) => !lineIds.has(item.skuId) && valueOf(item, SUB_BRAND_ATTR) === subBrand,
        );
      found.push({
        attr,
        value,
        name: ownSubBrand ? subBrand : value,
        side,
        skuIds: line.map((item) => item.skuId),
      });
    }
  }
  return found;
};

/** Replication check of one brand block's rows, and the lines that explain the base where the
 *  rows do not. `items` carry their own attributes and their base placements. `stackAttrs`: the
 *  attributes a row must not be stacked by on the base; absent, every line attribute.
 *  `preferredAttrs`: the category's declared levels; of two lines that explain the base with
 *  the same products, the one a declared level names goes first (Pack type "Soft"). */
export const replicateRows = (
  items: readonly StudioItem[],
  rowAttr: string,
  innerSize: boolean,
  lineAttrs: readonly string[],
  stackAttrs: readonly string[] = lineAttrs,
  preferredAttrs: ReadonlySet<string> | null = null,
): RowReplication => {
  const preferred = (line: RowLine) => Number(preferredAttrs?.has(line.attr) ?? false);
  const first = replicates(items, rowAttr, innerSize, stackAttrs);
  const lines: RowLine[] = [];
  let rest = [...items];
  let holds = first;
  while (!holds) {
    // The line after which the rest gives the base back explains it; of those, or failing any,
    // the fewest products: a line never takes more of the block than the base shows. Of lines
    // alike in both, one a declared level names.
    const candidates = edgeLinesOf(rest, rowAttr, lineAttrs).map((line) => {
      const taken = new Set(line.skuIds);
      const left = rest.filter((item) => !taken.has(item.skuId));
      return { line, explains: replicates(left, rowAttr, innerSize, stackAttrs) };
    });
    const line = [...candidates].sort(
      (a, b) =>
        Number(b.explains) - Number(a.explains) ||
        a.line.skuIds.length - b.line.skuIds.length ||
        preferred(b.line) - preferred(a.line),
    )[0]?.line;
    if (!line) break;
    lines.push(line);
    const taken = new Set(line.skuIds);
    rest = rest.filter((item) => !taken.has(item.skuId));
    holds = replicates(rest, rowAttr, innerSize, stackAttrs);
  }
  // Inside a row the size tier only orders the products (a store planogram stacks cdt2 rows, not
  // the sizes inside them), so only rows that still do not give the base back are a question.
  return { replicates: first, lines, unexplained: !holds && !rowsHold(rest, rowAttr) };
};

/** Every line row offer's flag id starts so. */
export const ROW_LINE_PREFIX = "row-line:";

/** The flag id of a line row offer: the brand block and the row's name. */
export const rowLineId = (column: string, name: string): string =>
  `${ROW_LINE_PREFIX}${column}:${name}`;

/** The flag id of a brand block whose rows nothing explains. */
export const rowQuestionId = (column: string): string => `row-question:${column}`;
